import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import {
	getCurrency,
	nullSafeGet,
	nullSafeGetOrElse,
	recordHasContractorId,
	compareStrings,
} from '../../utils/DataAccessUtils';
import { Alert, Button, Card, Col, Dropdown, Menu, message, Row, Popover } from 'antd';
import {
	isBuyerManagerOrInternalTech,
	isBuyerOrInternalTech,
	isInternalTech,
} from '../../utils/AuthUtils';
import {
	appendWorkOrderNotesForBuyer,
	appendWorkOrderNotesForSupplier,
} from '../../thunks/work_orders_thunks';
import { ROLE_TYPES } from '../../utils/DataConstants';
import {
	generateQuotePDF,
	quotesRestCrudThunksForBuyer,
	quotesRestCrudThunksForSupplier,
} from '../../thunks/quotes_thunks';
import QuoteEditButton from '../quote_edit_button/QuoteEditButton';
import ApprovalHierarchyDisplayQuote from '../approval_hierarchy_display_quote/ApprovalHierarchyDisplayQuote';
import CapexSwitch from '../capex_switch/CapexSwitch';
import QuotesAdditionalDetails from '../quotes_components/QuotesAdditionalDetails';
import QuoteUploadButton from '../quote_upload_button/QuoteUploadButton';
import QuotesDetailDisplay from '../quotes_detail_details_page/QuotesDetailDisplay';
import {
	proposalStatusChangeForBuyer,
	proposalStatusChangeForInternalSupplier,
	proposalStatusChangeForSupplier,
	syncProposalApprovalHierarchy,
} from '../../thunks/proposals_thunks';
import {
	markUserNotificationsReadForBuyer,
	markUserNotificationsReadForSupplier,
} from '../../thunks/user_notifications_thunks';
import { BUYER_ONLY_NO_STORE_ASSOCIATE } from '../roles/roles';
import { QUOTE_STATUSES } from '../../constants/QuoteStatuses';
import RequestAdditionalQuotesButton from './RequestAdditionalQuotesButton';
import FileUploader from '../file_uploader/FileUploader';
import {
	getQuoteRestCrudThunksContractorId,
	getQuoteSubURL,
	isClientQuoteView,
} from '../quote_utils/QuoteUtils';
import { getLastApprovedOrDisputedRevision } from '../invoice_utilities/InvoiceUtilities';
import { BLACKS_AND_WHITES } from '../../utils/DataFormatterUtils';
import { CostAndScopeBreakdown } from '../cost_and_scope_breakdown/CostAndScopeBreakdown';

const style = require('./QuotePageDisplay.less');

const Wrapper = ({ children, quote, cardWrapper }) =>
	cardWrapper ? (
		<Row style={{ margin: '16px 8px' }} gutter={12} key={quote.id} className="QuotePageDisplay">
			<Col span={24}>
				<Card>{children}</Card>
			</Col>
		</Row>
	) : (
		<span className="QuotePageDisplay">{children}</span>
	);

const QuotePageDisplay: FC<any> = ({
	quote = {},
	refreshQuote,
	showViewQuotePage = false,

	currentUser,
	history,
	userType,

	approveQuote,
	declineQuote,
	updateQuote,
	appendWorkOrderNotes,
	syncQuote,

	setAddingMarkup,
	setEditingQuote,
	setCopyQuote,
	isClientView = true,
	otherQuote = {},
	isDisplayOnly = false,
	cardWrapper = true,
	displayComparison,
	// Opt-in, quote detail page only: right-aligned at the top of the quote card.
	headerAction = null,
}) => {
	const [generatingPDF, setGeneratingPDF] = useState(false);
	const [deletingPDF, setDeletingPDF] = useState(false);
	const lastQuote = getLastApprovedOrDisputedRevision(quote);
	const showComparison = compareStrings(nullSafeGet('status', quote), QUOTE_STATUSES.pending)
		? lastQuote && displayComparison
		: false;

	const hasCreatedQuoteToMyClient = useMemo(() => otherQuote && !!otherQuote.id, [otherQuote]);

	const workOrder = useMemo(() => nullSafeGet('workOrder', quote), [quote]);
	const hasContractorId = useMemo(() => quote && recordHasContractorId(quote), [quote]);
	const currency = useMemo(
		() => getCurrency({ workOrder: nullSafeGet('workOrder', quote) }),
		[quote]
	);

	const generateQuote = useCallback(
		(id) => {
			setGeneratingPDF(true);
			generateQuotePDF(id, userType, currentUser, quote)
				.catch((error) => {
					message.error(error);
				})
				.finally(() => {
					setGeneratingPDF(false);
				});
		},
		[userType, currentUser, quote]
	);

	const handleQuotePDFUploadChange = useCallback(
		(quote) => (quoteFileUrl) => {
			const newQuote = {
				...quote,
				proposalPdfLink: quoteFileUrl,
			};
			if (quoteFileUrl === null) {
				setDeletingPDF(true);
			}
			updateQuote(newQuote)
				.then(() => {
					refreshQuote();
				})
				.catch((error) => {
					message.error(error);
				})
				.finally(() => {
					setDeletingPDF(false);
				});
		},
		[updateQuote, refreshQuote]
	);
	const approvalAllowed = nullSafeGetOrElse(
		'facility.internalTechConfig.allowedToApproveProposal',
		currentUser,
		false
	);
	const isAllowedToApprove =
		userType === ROLE_TYPES.BUYER || (isInternalTech(currentUser) && approvalAllowed);

	const hasPDF = useMemo(() => quote.proposalPdfLink && quote.proposalPdfLink.length > 0, [quote]);
	const hasUploadButton = useMemo(
		() =>
			[QUOTE_STATUSES.disputed, QUOTE_STATUSES.pending, QUOTE_STATUSES.draft].some(
				(status) => status === quote.status
			),
		[quote]
	);

	// Gate on THIS quote's own RFP link (requestForProposalId), not the work order's RFP history —
	// a work order can carry unrelated/stale rfpDataIds.
	const activeRFPAvailable = nullSafeGet('requestForProposalId', quote);
	const rfpAwarded = compareStrings(nullSafeGet('requestForProposal.status', quote), 'award');
	// Show the RFP context whenever this quote came from an RFP.
	const showRFPBlock = activeRFPAvailable && isBuyerOrInternalTech(currentUser);
	// Only gate the approval hierarchy while the RFP is still in progress (not yet awarded/completed).
	// Once the RFP is completed, the winning quote still has to clear the normal approval hierarchy.
	const rfpInProgress = showRFPBlock && !rfpAwarded;
	// The awarded/declined RFP messaging now lives in ApprovalHierarchyDisplayQuote (in lieu of its
	// generic approval message); here we only surface the under-review state for in-progress RFPs.
	const rfpStatusMessage = rfpAwarded ? null : 'This quote is currently under review in an RFP.';
	const rfpTitle = nullSafeGet('requestForProposal.title', quote);
	const rfpPartOfMessage = rfpTitle
		? `This quote is part of the RFP "${rfpTitle}".`
		: 'This quote is part of an RFP.';
	const viewRFPAction = isBuyerManagerOrInternalTech(currentUser) ? (
		<Link to={`/${userType}/requestsForProposal/detail/${quote.requestForProposalId}/details`}>
			<Button size="small">View RFP</Button>
		</Link>
	) : null;
	const path = getQuoteSubURL(userType, currentUser, quote);
	// @ts-ignore
	return (
		<Wrapper quote={quote} cardWrapper={cardWrapper}>
			{showRFPBlock && !isDisplayOnly ? (
				<Alert
					className="!mb-l"
					type="info"
					showIcon={true}
					message={rfpPartOfMessage}
					description={rfpStatusMessage}
					action={viewRFPAction}
				/>
			) : null}
			{headerAction ? <div className="mb-m flex justify-end">{headerAction}</div> : null}
			{isDisplayOnly ? null : (
				<Row justify={'space-between'} style={{ marginBottom: 16 }}>
					<Col style={{ flex: 1 }}>
						<div
							style={{
								display: 'flex',
								position: 'relative',
								height: 'fit-content',
								flexDirection: 'row',
								marginBottom: 20,
							}}
						>
							<div
								style={{
									position: 'relative',
									flexDirection: 'column',
									width: '100%',
								}}
							>
								<Row className="w-full justify-between align-top">
									<div className="flex flex-1 flex-col">
										{rfpInProgress ? null : (
											<ApprovalHierarchyDisplayQuote
												workOrder={workOrder}
												quote={quote}
												currentUser={currentUser}
												approveQuote={approveQuote}
												declineQuote={declineQuote}
												syncQuote={syncQuote}
												appendWorkOrderNotes={appendWorkOrderNotes}
												style={style}
												refresh={refreshQuote}
												history={history}
												allowedToApprove={isAllowedToApprove}
											/>
										)}
									</div>
									{isBuyerOrInternalTech(currentUser) || isClientView ? null : (
										<div className="flex flex-row justify-end">
											<Popover
												content={
													hasCreatedQuoteToMyClient
														? 'Warning: This will update your existing quote.'
														: null
												}
												placement={'left'}
											>
												<Dropdown
													overlay={
														<Menu>
															<Menu.Item
																onClick={() => {
																	setAddingMarkup(true);
																	setCopyQuote(quote);
																}}
															>
																Add Markup
															</Menu.Item>
															<Menu.Item
																onClick={() => {
																	setEditingQuote(true);
																	setCopyQuote(quote);
																}}
															>
																Edit and Copy
															</Menu.Item>
														</Menu>
													}
												>
													<Button style={{ marginRight: 12 }} onClick={(e) => e.preventDefault()}>
														Copy Quote
													</Button>
												</Dropdown>
											</Popover>
										</div>
									)}
								</Row>
							</div>
						</div>
					</Col>
					<Col style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-end' }}>
						{showRFPBlock ? null : (
							<RequestAdditionalQuotesButton workOrder={workOrder} quote={quote} />
						)}
						{showViewQuotePage ? (
							<span>
								<QuoteEditButton
									quote={quote}
									style={{ marginLeft: 12 }}
									onClick={() => {
										history.push(
											`/${userType}/workOrders/detail/${quote.workOrder.id}/editProposal${
												hasContractorId ? 'Contractor' : ''
											}/${quote.id}`
										);
									}}
								/>
							</span>
						) : null}
						{isBuyerOrInternalTech(currentUser) ? (
							<CapexSwitch
								quote={quote}
								refresh={refreshQuote}
								companyConfig={nullSafeGetOrElse(
									'workOrder.buyerCompany.buyerCompanySettings',
									quote,
									{}
								)}
							/>
						) : null}
					</Col>
				</Row>
			)}
			<CostAndScopeBreakdown
				showComparison={showComparison}
				label={'quote'}
				lastRecord={lastQuote}
			/>
			<QuotesAdditionalDetails
				quote={quote}
				lastQuote={lastQuote}
				passthroughItems={[
					isBuyerOrInternalTech(currentUser) || hasPDF || hasUploadButton
						? {
								key: 'File',
								value: (
									<QuoteUploadButton
										handleQuotePDFUploadChange={handleQuotePDFUploadChange}
										quote={quote}
										generatingPDF={generatingPDF}
										generateQuotePDF={generateQuote}
										deletingPDF={deletingPDF}
										isDisplayOnly={isDisplayOnly}
									/>
								),
								className: 'detailRow__item firstRow lastCol',
						  }
						: {},
				]}
				showComparison={showComparison}
			/>
			{nullSafeGet('attachments.0', quote) ? (
				<FileUploader
					roleType={userType}
					defaultFileList={nullSafeGetOrElse('attachments', quote, [])}
					convertedImages={nullSafeGet('convertedImages', quote)}
					uploaderType={'readOnly'}
				/>
			) : null}
			<QuotesDetailDisplay
				currency={currency}
				quote={quote}
				lastQuote={lastQuote}
				displayComparison={showComparison}
			/>
		</Wrapper>
	);
};

const mapStateToProps = (state) => ({
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	appendWorkOrderNotes: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? appendWorkOrderNotesForSupplier(entity)
				: appendWorkOrderNotesForBuyer(entity)
		),
	updateQuote: (entity) =>
		dispatch(
			getQuoteRestCrudThunksContractorId(
				ownProps.userType,
				recordHasContractorId(entity),
				isClientQuoteView(entity, ownProps.currentUser)
			).update(entity)
		),
	approveQuote: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? isInternalTech(ownProps.currentUser)
					? proposalStatusChangeForInternalSupplier('awarded', entity)
					: proposalStatusChangeForSupplier('awarded', entity)
				: proposalStatusChangeForBuyer('awarded', entity)
		),
	declineQuote: (entity) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? isInternalTech(ownProps.currentUser)
					? proposalStatusChangeForInternalSupplier('declined', entity)
					: proposalStatusChangeForSupplier('declined', entity)
				: proposalStatusChangeForBuyer('declined', entity)
		),
	syncQuote: (entity) => dispatch(syncProposalApprovalHierarchy(ownProps.userType, entity)),
	markUserNotificationsRead: (entityId) =>
		dispatch(
			ownProps.userType === ROLE_TYPES.SUPPLIER
				? markUserNotificationsReadForSupplier('Quote', entityId)
				: markUserNotificationsReadForBuyer('Quote', entityId)
		),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(QuotePageDisplay)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
		currentUser: (state as any).session.currentUser,
	}),
	() => ({})
)(ComponentWithoutUserType);
